import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Order, CustomerType, Warehouse } from '../types';
import { fetchOrders, pullOrdersFromWooCommerce, updateCustomerType } from '../utils/orderApi';
import useOrdersTable from '../hooks/useOrdersTable';
import OrderDetailsDialog from './OrderDetailsDialog';
import api from '../utils/api';
import { normalizeCarrier, getTrackingLink } from '../utils/carrierUtils';
import '../core.css';
import LoadingSpinner from './LoadingSpinner';

/** ISO time → "Oct 29, 2026, 8:45 AM EDT" in Eastern time. */
const formatEastern = (iso: string) => {
  if (!iso) return 'Not synced yet';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Toronto',
    timeZoneName: 'short',
  });
};

interface OrdersProps {
  isAdmin: boolean;
}

const Orders: React.FC<OrdersProps> = ({ isAdmin }) => {
  const { user, permissions } = useAuth();
  const { showToast } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [page, setPage] = useState(1);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [showOpenOrdersOnly, setShowOpenOrdersOnly] = useState(false);
  const [activeCustomerTypeDialog, setActiveCustomerTypeDialog] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [showDatePickerDialog, setShowDatePickerDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pullTracking, setPullTracking] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { displayedData, totalPages, sortConfig, searchTerm, setSearchTerm, handleSort } = useOrdersTable(orders, page);

  // When the last WooCommerce order sync finished (scheduled or manual)
  const fetchLastSynced = useCallback(async () => {
    try {
      const res = await api.get('/settings?key=orders_last_synced');
      setLastUpdated(res.data.orders_last_synced || '');
    } catch (err) {
      console.error('Failed to load last order sync time:', err);
    }
  }, []);

  const fetchNeonOrders = useCallback(async () => {
    setLoading(true);
    try {
      const pageNum = 1;
      const perPage = showAllOrders ? 0 : 1000;
      const statusFilter = showOpenOrdersOnly ? 'partial-shipped,processing,on-hold' : undefined;
      const response = await fetchOrders(pageNum, perPage, statusFilter);
      console.log('fetchNeonOrders API Response:', JSON.stringify(response, null, 2));
      const data = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
      let customerTypes = Array.isArray(response.customer_types) ? response.customer_types : [];
      if (!customerTypes.length) {
        console.log('No customer_types in /orders, fetching from /customer-types');
        try {
          const customerTypesResponse = await api.get('/customer-types');
          customerTypes = Array.isArray(customerTypesResponse.data.customer_types)
            ? customerTypesResponse.data.customer_types
            : [];
          console.log('Fetched Customer Types:', JSON.stringify(customerTypes, null, 2));
        } catch (apiError: any) {
          console.error('Failed to fetch customer types:', apiError);
        }
      }
      const mappedOrders = data.map((order: Order) => ({
        ...order,
        customer_type: order.customer_type || null,
        customer_type_color: customerTypes.find((type: CustomerType) => type.name === order.customer_type)?.color || '#e0e0e0',
        shipments: Array.isArray(order.shipments)
          ? order.shipments.filter((s: any) => s.id !== null).map((s: any) => ({
              ...s,
              items: Array.isArray(s.items) ? s.items.filter((i: any) => i.id !== null) : [],
            }))
          : [],
        items: Array.isArray(order.items) ? order.items.filter((i: any) => i.id !== null) : [],
      }));
      console.log('Mapped Orders:', JSON.stringify(mappedOrders.map(o => ({
        id: o.id,
        customer_type: o.customer_type,
        customer_type_color: o.customer_type_color,
      })), null, 2));
      console.log('Customer Types:', JSON.stringify(customerTypes, null, 2));
      setOrders(mappedOrders);
      setCustomerTypes(customerTypes);
      await fetchLastSynced();
    } catch (error: any) {
      console.error('Fetch orders error:', error, error.response?.data);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        const errorDetails = error.response?.data?.error || error.message || 'Unknown error';
        const toastMessage = `Failed To Fetch Orders - Error Details: ${errorDetails}`;
        setError(toastMessage);
        showToast(toastMessage, 'error', 10000);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, showAllOrders, showOpenOrdersOnly, fetchLastSynced]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await api.get('/neon-stock');
      console.log('fetchWarehouses API Response:', JSON.stringify(response.data, null, 2));
      setWarehouses(response.data.warehouses || []);
    } catch (error: any) {
      console.error('Fetch warehouses error:', error);
      const errorDetails = error.response?.data?.error || error.message || 'Unknown error';
      const toastMessage = `Failed To Fetch Warehouses - Error Details: ${errorDetails}`;
      setError(toastMessage);
      showToast(toastMessage, 'error', 10000);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      console.log('No user, redirecting to /login');
      navigate('/login');
      return;
    }
    fetchNeonOrders();
    fetchWarehouses();
  }, [user, navigate, fetchNeonOrders, fetchWarehouses, showAllOrders, showOpenOrdersOnly]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        console.log('Closing dialog due to click outside');
        setActiveCustomerTypeDialog(null);
        setSelectedOrder(null);
        setShowDatePickerDialog(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pullOrders = async () => {
    if (!isAdmin) return;
    setUpdating(true);
    try {
      const response = await pullOrdersFromWooCommerce({
        start_date: startDate,
        end_date: endDate,
        pull_tracking: pullTracking.toString(),
      });
      const data = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
      const mappedOrders = data.map((order: Order) => ({
        ...order,
        shipments: Array.isArray(order.shipments)
          ? order.shipments.filter((s: any) => s.id !== null).map((s: any) => ({
              ...s,
              items: Array.isArray(s.items) ? s.items.filter((i: any) => i.id !== null) : [],
            }))
          : [],
        items: Array.isArray(order.items) ? order.items.filter((i: any) => i.id !== null) : [],
      }));
      console.log('pullOrders Mapped Orders:', JSON.stringify(mappedOrders, null, 2));
      setOrders(mappedOrders);
      setLastUpdated(response.last_updated);
      setError(null);
      setShowDatePickerDialog(false);
    } catch (error: any) {
      console.error('Pull orders error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        const errorDetails = error.response?.data?.details || error.response?.data?.error || error.message || 'Unknown error';
        const toastMessage = `Failed To Pull WooCommerce Orders - Error Details: ${errorDetails}`;
        setError(toastMessage);
        showToast(toastMessage, 'error', 10000); // Show for 10 seconds since it's detailed
      }
    } finally {
      setUpdating(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const openCustomerTypeDialog = (order: Order, event: React.MouseEvent) => {
    if (!isAdmin) return;
    event.stopPropagation();
    console.log('openCustomerTypeDialog called for order:', order.id);
    setActiveCustomerTypeDialog(`${order.id}`);
    setSelectedOrder(order);
    console.log('Set activeCustomerTypeDialog to:', `${order.id}`, 'Selected order:', order.id);
  };

  const closeCustomerTypeDialog = () => {
    console.log('Closing customer type dialog');
    setActiveCustomerTypeDialog(null);
    setSelectedOrder(null);
  };

  const handleCustomerTypeSelect = async (customerTypeName: string) => {
    if (!activeCustomerTypeDialog || !selectedOrder) {
      console.error('No active customer type dialog or selected order');
      setError('Cannot update customer type: No order selected');
      return;
    }
    console.log('handleCustomerTypeSelect:', {
      customerTypeName,
      customerName: selectedOrder.customer_name,
      orderId: selectedOrder.id,
    });
    try {
      await updateCustomerType(
        selectedOrder.id,
        customerTypeName === 'None' ? '' : customerTypeName,
        selectedOrder.customer_name
      );
      const selectedType = customerTypes.find((type) => type.name === customerTypeName);
      setOrders((prev) =>
        prev.map((order) =>
          order.customer_name === selectedOrder.customer_name
            ? {
                ...order,
                customer_type: customerTypeName === 'None' ? null : customerTypeName,
                customer_type_color: selectedType ? selectedType.color : '#e0e0e0',
              }
            : order
        )
      );
      console.log('Updated Orders:', {
        customerName: selectedOrder.customer_name,
        customerType: customerTypeName,
        color: selectedType ? selectedType.color : '#e0e0e0',
      });
    } catch (error: any) {
      console.error('Failed to update customer type:', error, error.response?.data);
      const errorDetails = error.response?.data?.error || error.message || 'Unknown error';
      const toastMessage = `Failed To Update Customer Type - Error Details: ${errorDetails}`;
      setError(toastMessage);
      showToast(toastMessage, 'error', 10000);
    }
    closeCustomerTypeDialog();
  };

  const openDatePickerDialog = () => {
    if (!isAdmin) return;
    setShowDatePickerDialog(true);
    setStartDate('');
    setEndDate('');
    setPullTracking(false);
  };

  const closeDatePickerDialog = () => {
    setShowDatePickerDialog(false);
  };

  const openOrderDetailsDialog = (order: Order) => {
    if (activeCustomerTypeDialog) {
      console.log('Blocked openOrderDetailsDialog: CustomerTypeDialog is active');
      return;
    }
    console.log('openOrderDetailsDialog called for order:', order.id);
    setActiveOrder(order);
    setTimeout(() => {
      console.log('State after openOrderDetailsDialog:', { activeCustomerTypeDialog, activeOrder });
    }, 0);
  };

  const closeOrderDetailsDialog = () => {
    setActiveOrder(null);
  };

  // Deep link from a task: /orders?order=ID opens that order if it's in the
  // loaded set, otherwise searches for it.
  const location = useLocation();
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('order');
    if (!id || loading || !orders.length) return;
    const match = orders.find((o) => String(o.id) === id);
    if (match) setActiveOrder(match);
    else setSearchTerm(id);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, loading, orders, navigate, setSearchTerm]);

  const capitalizeStatus = (status: string) => {
    if (!status) return '';
    // Capitalise each part so "on-hold" → "On-Hold", matching the chip colours in core.css
    return status
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('-');
  };



  // Function to get tracking chips for an item with distribution logic
  const getTrackingChipsForItem = (itemId: number, shipments: any[]) => {
    const chips: JSX.Element[] = [];

    const relevantShipments = Array.isArray(shipments)
      ? shipments.filter(s => Array.isArray(s.items) && s.items.some((si: { order_item_id: number }) => si.order_item_id === itemId))
      : [];

    relevantShipments.forEach(shipment => {
      const trackingNumbers = shipment.tracking_number ? shipment.tracking_number.split(',').map((s: string) => s.trim()) : [];
      if (trackingNumbers.length === 0) return;

      // Find the shipment item for this itemId
      const shipmentItem = shipment.items.find((si: { order_item_id: number }) => si.order_item_id === itemId);
      if (!shipmentItem) return;

      // Calculate total items in this shipment
      const totalItems = shipment.items.reduce((sum: number, si: any) => sum + si.quantity, 0);

      // Find the index of this shipment item
      const siIndex = shipment.items.findIndex((si: { order_item_id: number }) => si.order_item_id === itemId);
      const itemIndex = shipment.items.slice(0, siIndex).reduce((sum: number, si: any) => sum + si.quantity, 0);

      let chipsToShow: string[];
      if (trackingNumbers.length > totalItems) {
        // More tracking numbers than items: show all tracking numbers
        chipsToShow = trackingNumbers;
      } else {
        // Equal or fewer: show one tracking number based on distribution
        chipsToShow = [trackingNumbers[itemIndex % trackingNumbers.length]];
      }

      chipsToShow.forEach(trackingNumber => {
        chips.push(
          <a
            key={`${shipment.id}-${trackingNumber}`}
            href={getTrackingLink(trackingNumber, shipment.carrier)}
            target="_blank"
            rel="noopener noreferrer"
            className={`carrier-chip carrier-chip-${normalizeCarrier(shipment.carrier).toLowerCase()}`}
            onClick={(e) => e.stopPropagation()}
          >
            {trackingNumber}
          </a>
        );
      });
    });

    return chips.length > 0 ? chips : null;
  };

  const columns = [
    { key: 'id', label: 'Order', sortKey: 'id' as const },
    { key: 'date_created', label: 'Date', sortKey: 'date_created' as const },
    { key: 'customer_name', label: 'Customer Name', sortKey: 'customer_name' as const },
    { key: 'customer_type', label: '', sortKey: null },
    { key: 'items', label: 'Items', sortKey: null },
    { key: 'status', label: 'Status', sortKey: 'status' as const },
    { key: 'total', label: 'Total', sortKey: 'total' as const },
  ];

  if (error) return <div className="container">Error: {error}</div>;
  if (loading) return <LoadingSpinner />;

  return (
    <div className="stock-table-container">
      <h1>Orders</h1>
      <p className="last-updated">Last Updated: {formatEastern(lastUpdated)}</p>
      {isAdmin && (
        <div className="pull-data-container">
          <button onClick={openDatePickerDialog} className="action-button" disabled={updating}>
            Pull Orders
          </button>
          {updating && <span className="updating-text">Updating...</span>}
        </div>
      )}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search orders (e.g., ID, customer, status, product)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <div className="filter-container">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showAllOrders}
            onChange={(e) => {
              setShowAllOrders(e.target.checked);
              setPage(1);
            }}
          />
          Show All Orders
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showOpenOrdersOnly}
            onChange={(e) => {
              setShowOpenOrdersOnly(e.target.checked);
              setPage(1);
            }}
          />
          Show Open Orders Only
        </label>
      </div>
      <div className="table-header">
        <p>Loaded Orders: {orders.length}</p>
        <div className="pagination">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={col.sortKey ? () => handleSort(col.sortKey) : undefined}
                style={{ cursor: col.sortKey ? 'pointer' : 'default', width: col.key === 'customer_type' ? '50px' : 'auto' }}
              >
                {col.label}
                {sortConfig.key === col.sortKey && (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedData.map((order) => (
            <tr key={order.id}>
              <td
                data-label="Order"
                className="clickable-column po-number-cell"
                onClick={() => openOrderDetailsDialog(order)}
                style={{ cursor: 'pointer' }}
              >
                {order.id}
              </td>
              <td data-label="Date">
                {new Date(order.date_created).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </td>
              <td
                data-label="Customer Name"
                className="clickable-column"
                onClick={() => openOrderDetailsDialog(order)}
                style={{ cursor: 'pointer' }}
              >
                {order.customer_name}
              </td>
              <td
                data-label=""
                style={{ cursor: 'default' }}
                onClick={() => console.log('Customer Type TD clicked, should do nothing')}
              >
                <span
                  className="supplier-tag customer-type-tag"
                  style={{ backgroundColor: order.customer_type_color || '#e0e0e0', cursor: isAdmin ? 'pointer' : 'default' }}
                  onClick={(e) => openCustomerTypeDialog(order, e)}
                >
                  {order.customer_type || 'None'}
                </span>
              </td>
              <td
                data-label="Items"
                className="clickable-column"
                style={{ textAlign: 'left', cursor: 'pointer' }}
                onClick={() => openOrderDetailsDialog(order)}
              >
                {order.items?.map((item) => (
                  <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px'}}>
                    <span>{`(${item.quantity}x) ${item.product_name}`}</span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {getTrackingChipsForItem(item.id, order.shipments) || ''}
                    </div>
                  </div>
                )) || 'No items'}
              </td>
              <td data-label="Status">
                <span
                  className="supplier-tag"
                  data-status={capitalizeStatus(order.status)}
                >
                  {capitalizeStatus(order.status)}
                </span>
              </td>
              <td data-label="Total">${(Number(order.total) || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {activeCustomerTypeDialog && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog" ref={dialogRef}>
            <h3>Select Customer Type</h3>
            {customerTypes.length === 0 ? (
              <p>No customer types available. Check database or API.</p>
            ) : (
              <ul className="supplier-list">
                {customerTypes.map((type) => (
                  <li
                    key={type.id}
                    className="supplier-item"
                    style={{ backgroundColor: type.color }}
                    onClick={() => handleCustomerTypeSelect(type.name)}
                  >
                    {type.name}
                  </li>
                ))}
              </ul>
            )}
            <div className="supplier-dialog-actions">
              <button
                className="action-button cancel-button"
                onClick={closeCustomerTypeDialog}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDatePickerDialog && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog" ref={dialogRef}>
            <button className="close-button" onClick={closeDatePickerDialog}>
              ✕
            </button>
            <h3>Select Date Range</h3>
            <div className="date-picker-container">
              <label>
                Start Date:
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                />
              </label>
              <label>
                End Date:
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={pullTracking}
                  onChange={(e) => setPullTracking(e.target.checked)}
                />
                Pull Order Tracking
              </label>
            </div>
            <div className="supplier-dialog-actions">
              <button
                className="action-button"
                onClick={pullOrders}
                disabled={updating || !startDate || !endDate}
              >
                Pull Orders
              </button>
            </div>
          </div>
        </div>
      )}

      {activeOrder && (
        <OrderDetailsDialog
          order={activeOrder}
          onClose={closeOrderDetailsDialog}
          warehouses={warehouses}
          refreshOrders={fetchNeonOrders}
        />
      )}
    </div>
  );
};

export default Orders;